import { useState, type JSX } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Dialog } from '../../components/Dialog';
import { useActions, useProject, useUiContext } from '../../context';
import { attempt, safely } from '../../safe';

/** Source kinds whose capture prompt must be opened from the click that adds them. */
const GESTURE_START_KINDS = new Set(['display-capture']);

/** "Create new" (with a name) or "Add existing" (another reference to a source of this kind). */
export function AddSourceDialog({ kind, onClose }: { kind: string; onClose(): void }): JSX.Element {
  const { studio, ui } = useUiContext();
  const actions = useActions();
  const def = studio.registry.getSource(kind);
  const existing = useProject(
    useShallow((p) =>
      Object.values(p.sources)
        .filter((s) => s.kind === kind)
        .map((s) => `${s.id}\n${s.name}`),
    ),
  ).map((pair) => {
    const split = pair.indexOf('\n');
    return { id: pair.slice(0, split), name: pair.slice(split + 1) };
  });
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [name, setName] = useState(() => studio.queries.uniqueName(studio.project.getState().project, def?.displayName ?? kind, 'sources'));
  const [picked, setPicked] = useState<string>(existing[0]?.id ?? '');
  const [visible, setVisible] = useState(true);

  const sceneId = actions.editedSceneId();
  const valid = !!sceneId && (mode === 'new' ? name.trim().length > 0 : picked !== '');

  const add = (): void => {
    if (!sceneId || !valid) return;
    if (mode === 'existing') {
      const itemId = attempt(studio, () => studio.commands.addExistingSource(sceneId, picked, { visible }));
      if (itemId) ui.getState().select([itemId]);
      onClose();
      return;
    }
    const created = attempt(studio, () => studio.commands.addSourceToScene(sceneId, kind, { name: name.trim(), visible }));
    if (!created) return;
    // Screen capture may only be requested from a user gesture; this click is that gesture.
    if (GESTURE_START_KINDS.has(kind)) void safely(studio, () => studio.invokeSource(created.sourceId, 'start'));
    ui.getState().select([created.itemId]);
    onClose();
    ui.getState().openDialog({ type: 'properties', sourceId: created.sourceId });
  };

  return (
    <Dialog
      title={`Add ${def?.displayName ?? kind}`}
      onClose={onClose}
      width={440}
      footer={
        <>
          <button type="button" className="ui-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ui-btn primary" disabled={!valid} onClick={add}>
            Add
          </button>
        </>
      }
    >
      <form
        className="ui-col"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        {!def ? <div className="ui-warn-text">The source type "{kind}" is not registered.</div> : null}
        <label className="ui-check">
          <input type="radio" name="add-mode" checked={mode === 'new'} onChange={() => setMode('new')} />
          Create new
        </label>
        <input
          className="ui-input"
          data-autofocus
          aria-label="Name of the new source"
          value={name}
          disabled={mode !== 'new'}
          onChange={(e) => setName(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
        />
        <label className="ui-check">
          <input type="radio" name="add-mode" checked={mode === 'existing'} disabled={existing.length === 0} onChange={() => setMode('existing')} />
          Add existing {existing.length === 0 ? <span className="ui-dim">(none yet)</span> : null}
        </label>
        <select
          className="ui-select"
          size={Math.min(6, Math.max(2, existing.length))}
          aria-label="Existing sources of this type"
          disabled={mode !== 'existing'}
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
        >
          {existing.map((source) => (
            <option key={source.id} value={source.id}>
              {source.name}
            </option>
          ))}
        </select>
        <label className="ui-check">
          <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
          Make source visible
        </label>
      </form>
    </Dialog>
  );
}
